/** @jsxImportSource @emotion/react */
import { keyframes } from '@emotion/react';
import {
  createStyles,
  Icon,
  useTheme,
  Tooltip,
} from '@quickbit/qb-design-system';
import { TransactionType } from 'types/transactions';

interface Props {
  status: string;
  transactionType?: TransactionType;
}

const RenderStatusTooltip = ({ status, transactionType }: Props) => {
  const styles = useStyles();
  const { spacing } = useTheme();

  const needsTravelRuleAction =
    status === 'PENDING_TRAVEL_RULE' &&
    transactionType === TransactionType.RECEIVE_EXTERNAL;

  if (needsTravelRuleAction) {
    return (
      <Tooltip
        title="Action required: Provide originator information to complete this transaction"
        placement="top"
      >
        <div css={styles.actionRequiredContainer}>
          <div css={[styles.dot, styles.actionRequiredDot]} />
          <span css={styles.actionRequiredText}>Action Required</span>
        </div>
      </Tooltip>
    );
  }

  switch (status) {
    case 'FAILED':
      return (
        <Tooltip
          title="Transaction failed. Please try again later."
          placement="top"
        >
          <Icon name="error" size={spacing.xxs} />
        </Tooltip>
      );
    case 'MANUAL_REVIEW':
    case 'PENDING':
    case 'PENDING_TRAVEL_RULE':
      return (
        <Tooltip
          title="Due to regulatory requirements, processing this transaction may take additional time."
          placement="top"
        >
          <div css={[{ ...styles.dot }, { ...styles.statusPending }]} />
        </Tooltip>
      );
    default:
      return (
        <Tooltip title="Transaction completed." placement="top">
          <div css={styles.statusSuccess}>
            <Icon name="checkmark" size={spacing.xxs} />
          </div>
        </Tooltip>
      );
  }
};

export default RenderStatusTooltip;

const useStyles = createStyles(({ palette, spacing }) => ({
  dot: {
    height: spacing.xxxs,
    width: spacing.xxxs,
    borderRadius: '50%',
    boxShadow: '0 0 0 0 rgba(255, 177, 66, 1)',
    display: 'inline-block',
    transform: 'scale(1)',
    animation: `${keyframes`
      0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(255, 177, 66, 0.7);
      }
      70% {
        transform: scale(1);
        box-shadow: 0 0 0 ${spacing.xxxs} rgba(255, 177, 66, 0);
      }
      100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(255, 177, 66, 0);
      }
      `} 2s infinite`,
  },
  statusSuccess: {
    svg: {
      fill: 'none',
      stroke: palette.success.default,
    },
  },
  statusPending: {
    backgroundColor: palette.warning.default,
  },
  actionRequiredContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xxxs,
  },
  actionRequiredDot: {
    backgroundColor: palette.danger.default,
    boxShadow: '0 0 0 0 rgba(220, 53, 69, 1)',
    animation: `${keyframes`
      0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
      }
      70% {
        transform: scale(1);
        box-shadow: 0 0 0 ${spacing.xxxs} rgba(220, 53, 69, 0);
      }
      100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
      }
      `} 1.5s infinite`,
  },
  actionRequiredText: {
    whiteSpace: 'nowrap',
    fontWeight: 600,
    fontSize: '0.75rem',
    color: palette.danger.default,
  },
}));
